import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { key:'visitor', label:'Visitor', icon:'🧑‍🤝‍🧑' },
  { key:'guest_house', label:'Guest House', icon:'🏠' },
  { key:'employee', label:'Employee', icon:'👔' },
  { key:'vehicle', label:'Vehicle', icon:'🚗' },
  { key:'courier', label:'Courier', icon:'📦' },
  { key:'other', label:'Other', icon:'📋' },
];
const CATEGORY_ICONS = Object.fromEntries(CATEGORIES.map(c => [c.key, c.icon]));

const EMPTY = {
  expense_type:'', expense_category:'other',
  amount:'', expense_date: new Date().toISOString().split('T')[0],
  remarks:'',
  company_id:'', unit_id:'', department:'',
  reference_employee_id:'', visitor_id:'', booking_id:'',
  vehicle_id:'', courier_reference:''
};

export default function Expenses() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filterDate, setFilterDate] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Reference data
  const [employees, setEmployees] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [units, setUnits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [empSearch, setEmpSearch] = useState('');

  // Auto-filled
  const [autoFilled, setAutoFilled] = useState({});

  const buildQuery = () => {
    const p = [];
    if (filterDate) p.push(`date=${filterDate}`);
    if (filterCategory) p.push(`type=${filterCategory}`);
    if (filterFrom) p.push(`from=${filterFrom}`);
    if (filterTo) p.push(`to=${filterTo}`);
    return p.join('&');
  };

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get(`/api/expenses?${buildQuery()}`); setExpenses(r.data); }
    catch (err) { console.error('expenses:', err); setExpenses([]); }
    try { const r = await api.get('/api/expenses/summary'); setSummary(r.data); }
    catch (err) { console.error('summary:', err); setSummary([]); }
    try { const r = await api.get('/api/employees/active'); setEmployees(r.data); }
    catch (err) { console.error('employees:', err); setEmployees([]); }
    if (isAdmin) {
      try { const r = await api.get('/api/companies'); setCompanies(r.data); }
      catch (err) { console.error('companies:', err); setCompanies([]); }
    }
    try { const r = await api.get('/api/units'); setUnits(r.data); }
    catch (err) { console.error('units:', err); setUnits([]); }
    setLoading(false);
  };

  // Load visitors and bookings lazily when form opens
  const loadFormRefs = async () => {
    try { const r = await api.get('/api/visitors?status=active'); setVisitors(r.data.slice(0,200)); } catch {}
    try { const r = await api.get('/api/rooms/bookings?status=checked_in'); setBookings(r.data.slice(0,200)); } catch {}
    try { const r = await api.get('/api/vehicles'); setVehicles(r.data.slice(0,100)); } catch {}
  };

  useEffect(() => { if (user) load(); }, [user?.role, filterDate, filterCategory, filterFrom, filterTo]);

  const openForm = (exp = null) => {
    loadFormRefs();
    if (exp) {
      setForm({
        expense_type: exp.expense_type || '',
        expense_category: exp.expense_category || 'other',
        amount: exp.amount || '',
        expense_date: exp.expense_date || new Date().toISOString().split('T')[0],
        remarks: exp.remarks || '',
        company_id: exp.company_id || '',
        unit_id: exp.unit_id || '',
        department: exp.department || '',
        reference_employee_id: exp.reference_employee_id || '',
        visitor_id: exp.visitor_id || '',
        booking_id: exp.booking_id || '',
        vehicle_id: exp.vehicle_id || '',
        courier_reference: exp.courier_reference || ''
      });
      setEditing(exp.id);
    } else {
      setForm(EMPTY); setEditing(null);
    }
    setAutoFilled({});
    setEmpSearch('');
    setShowForm(true);
  };

  const handleEmployeeChange = (empId) => {
    setForm(f => ({ ...f, reference_employee_id: empId }));
    if (!empId) { setAutoFilled({}); return; }
    const emp = employees.find(e => e.id === parseInt(empId));
    if (emp) {
      setAutoFilled({ department: emp.department, company: emp.company_name, unit: emp.unit_name });
      setForm(f => ({
        ...f, reference_employee_id: empId,
        department: emp.department || f.department,
        company_id: emp.company_id ? String(emp.company_id) : f.company_id,
      }));
    }
  };

  const handleVisitorChange = (visId) => {
    setForm(f => ({ ...f, visitor_id: visId }));
    if (!visId) return;
    const vis = visitors.find(v => v.id === parseInt(visId));
    if (vis?.company_id) {
      setForm(f => ({ ...f, visitor_id: visId, company_id: String(vis.company_id) }));
      setAutoFilled(a => ({ ...a, visitorCompany: vis.company_name_resolved || vis.company_name }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        reference_employee_id: form.reference_employee_id ? parseInt(form.reference_employee_id) : null,
        visitor_id: form.visitor_id ? parseInt(form.visitor_id) : null,
        booking_id: form.booking_id ? parseInt(form.booking_id) : null,
        vehicle_id: form.vehicle_id ? parseInt(form.vehicle_id) : null,
        company_id: form.company_id ? parseInt(form.company_id) : null,
        unit_id: form.unit_id ? parseInt(form.unit_id) : null,
      };
      if (editing) await api.put(`/api/expenses/${editing}`, payload);
      else await api.post('/api/expenses', payload);
      setShowForm(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await api.delete(`/api/expenses/${id}`); load(); }
    catch { alert('Delete failed'); }
  };

  const total = expenses.reduce((s, e) => s + parseFloat(e.amount||0), 0);

  const filteredEmployees = employees.filter(e =>
    !empSearch || e.name.toLowerCase().includes(empSearch.toLowerCase()) ||
    (e.employee_code||'').toLowerCase().includes(empSearch.toLowerCase())
  );

  const exportCSV = () => {
    const header = 'Date,Category,Type,Amount,Company,Employee,Guest,Remarks\n';
    const rows = expenses.map(e =>
      [e.expense_date, e.expense_category, e.expense_type, e.amount,
       e.company_name||'', e.employee_name||'', e.guest_name||e.visitor_name||'',
       (e.remarks||'').replace(/,/g,'')].join(',')
    ).join('\n');
    const blob = new Blob([header+rows], {type:'text/csv'});
    const a = document.createElement('a'); a.href=URL.createObjectURL(blob); a.download='expenses.csv'; a.click();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Expenses</h1>
          <p className="text-gray-500 text-sm">Total: PKR {total.toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCSV} className="border border-gray-300 px-3 py-2 rounded-lg text-sm hover:bg-gray-50">📥 Export</button>
          <button onClick={() => openForm(null)}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ➕ Add Expense
          </button>
        </div>
      </div>

      {/* Category Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CATEGORIES.map(cat => {
          const s = summary.find(x => x.expense_type === cat.key);
          return (
            <div key={cat.key}
              onClick={() => setFilterCategory(filterCategory === cat.key ? '' : cat.key)}
              className={`bg-white rounded-xl border p-3 text-center cursor-pointer transition-all ${
                filterCategory === cat.key ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-100 hover:shadow-sm'
              }`}>
              <div className="text-xl mb-1">{cat.icon}</div>
              <p className="text-xs text-gray-500 font-medium">{cat.label}</p>
              <p className="text-sm font-bold text-gray-800 mt-1">
                PKR {s ? Number(s.total).toLocaleString() : '0'}
              </p>
              <p className="text-xs text-gray-400">{s ? s.count : '0'} entries</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
        <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} placeholder="Date"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} placeholder="From"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} placeholder="To"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        {(filterDate||filterCategory||filterFrom||filterTo) && (
          <button onClick={() => { setFilterDate(''); setFilterCategory(''); setFilterFrom(''); setFilterTo(''); }}
            className="text-red-500 text-sm hover:underline">Clear</button>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold">{editing ? 'Edit Expense' : 'Add Expense'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-2 gap-4">
              {/* Category + Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select value={form.expense_category}
                  onChange={e => setForm({...form,expense_category:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {CATEGORIES.map(c => (
                    <option key={c.key} value={c.key}>{c.icon} {c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expense Description *</label>
                <input value={form.expense_type}
                  onChange={e => setForm({...form,expense_type:e.target.value})} required
                  placeholder="e.g. Lunch, Fuel, Transport"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (PKR) *</label>
                <input type="number" step="0.01" value={form.amount}
                  onChange={e => setForm({...form,amount:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" value={form.expense_date}
                  onChange={e => setForm({...form,expense_date:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              {/* Company + Unit */}
              <div className="col-span-2 border-t pt-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Company / Department</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                {isAdmin ? (
                  <select value={form.company_id}
                    onChange={e => setForm({...form,company_id:e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— Select Company —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <input type="text" disabled
                    value={user?.company_name || 'Your Company'}
                    className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <select value={form.unit_id}
                  onChange={e => setForm({...form,unit_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">— Select Unit —</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input value={form.department}
                  onChange={e => setForm({...form,department:e.target.value})}
                  placeholder="Auto-filled from employee or enter manually"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              {/* Reference Employee */}
              <div className="col-span-2 border-t pt-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Reference (Optional)</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference Employee</label>
                <input type="text" placeholder="Search employee..."
                  value={empSearch} onChange={e => setEmpSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-1" />
                <select value={form.reference_employee_id}
                  onChange={e => handleEmployeeChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">— No employee —</option>
                  {(empSearch ? filteredEmployees : filteredEmployees.slice(0, 500)).map(e => (
                    <option key={e.id} value={e.id}>
                      {e.name} {e.employee_code ? `(${e.employee_code})` : ''} — {e.department||''}
                    </option>
                  ))}
                  {!empSearch && filteredEmployees.length > 500 && (
                    <option disabled>— Type above to search all {filteredEmployees.length} employees —</option>
                  )}
                </select>
                {(autoFilled.department || autoFilled.company) && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ Auto-filled: {[autoFilled.department, autoFilled.unit, autoFilled.company].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>

              {/* Visitor reference */}
              {(form.expense_category === 'visitor' || form.visitor_id) && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visitor</label>
                  <select value={form.visitor_id}
                    onChange={e => handleVisitorChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— Select Visitor —</option>
                    {visitors.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.visitor_name} — {v.company_name||'N/A'} ({v.status})
                      </option>
                    ))}
                  </select>
                  {autoFilled.visitorCompany && (
                    <p className="text-xs text-green-600 mt-1">🏢 {autoFilled.visitorCompany}</p>
                  )}
                </div>
              )}

              {/* Guest house booking reference */}
              {(form.expense_category === 'guest_house' || form.booking_id) && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guest House Booking</label>
                  <select value={form.booking_id}
                    onChange={e => setForm({...form,booking_id:e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— Select Booking —</option>
                    {bookings.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.guest_name} — {b.room_name||'Room'} ({b.check_in})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Courier reference */}
              {(form.expense_category === 'courier') && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Courier Reference (Tracking No.)</label>
                  <input value={form.courier_reference}
                    onChange={e => setForm({...form,courier_reference:e.target.value})}
                    placeholder="Tracking number or reference"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              )}

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea value={form.remarks}
                  onChange={e => setForm({...form,remarks:e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div className="col-span-2 flex gap-3 pt-2 border-t">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Add Expense'}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : expenses.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">💰</div>
            <p>No expenses found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>{['Date','Category','Description','Company','Dept','Reference','Amount','Remarks',''].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {expenses.map(exp => (
                  <tr key={exp.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-gray-600">{new Date(exp.expense_date).toLocaleDateString()}</td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1">
                        <span>{CATEGORY_ICONS[exp.expense_category]||'📋'}</span>
                        <span className="text-xs">{exp.expense_category}</span>
                      </span>
                    </td>
                    <td className="px-3 py-3 font-medium">{exp.expense_type}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{exp.company_name||'—'}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{exp.employee_department||exp.department||'—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {exp.employee_name && <div>👔 {exp.employee_name}</div>}
                      {exp.visitor_name && <div>👤 {exp.visitor_name}</div>}
                      {exp.guest_name && <div>🏠 {exp.guest_name}</div>}
                    </td>
                    <td className="px-3 py-3 font-semibold text-green-700 whitespace-nowrap">
                      PKR {Number(exp.amount).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-gray-500 text-xs max-w-xs truncate">{exp.remarks||'—'}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => openForm(exp)} className="text-blue-500 hover:text-blue-700 p-1">✏️</button>
                        <button onClick={() => handleDelete(exp.id)} className="text-red-400 hover:text-red-600 p-1">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td colSpan={6} className="px-3 py-3 text-gray-700">Total ({expenses.length} entries)</td>
                  <td className="px-3 py-3 text-green-700">PKR {total.toLocaleString()}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
